import React, { useState, useEffect } from 'react';
import { Layout, Card, Typography, Select, Input, Button, message, Tag, Table, Checkbox, Space } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface Producto {
  id: number;
  nombre: string;
  descripcion?: string;
  nivel?: string;
}

interface TicketData {
  id: number; // ID interno único que empieza desde 1
  numero_ticket: number;
  rollo_identificacion: string;
  cliente: string;
  cliente_id?: number; // ID del cliente
  productor?: string; // Nombre del productor
  productor_id?: number; // ID del productor
  kilos_netos: number;
  desmotado?: boolean; // Indica si el rollo ya está desmotado
  selected?: boolean;
}

interface TicketResponse {
  status: string;
  data: TicketData[];
  message?: string;
}

const PrevioDesmote: React.FC = () => {
  const { user, getAccessTokenSilently, isLoading: authLoading } = useAuth0();
  const navigate = useNavigate();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<number | null>(null);
  const [ticketBalanza, setTicketBalanza] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [ticketsData, setTicketsData] = useState<TicketData[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const empresaId = user?.id_empresa;
  const sucursalId = user?.id_sucursal;
  
  // Log para debug del objeto user
  useEffect(() => {
    if (user) {
      console.log('Objeto user completo:', user);
      console.log('empresaId extraído:', empresaId);
      console.log('sucursalId extraído:', sucursalId);
    }
  }, [user, empresaId, sucursalId]);
  
  // Cargar productos desde el backend
  useEffect(() => {
    const cargarProductos = async () => {
      try {
        setLoadingProductos(true);
        
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/productos/tipo/2`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const data = await response.json();
        console.log('Respuesta productos completa:', data);
        
        // Si data es directamente el array de productos
        if (Array.isArray(data)) {
          console.log('Data es un array:', data);
          setProductos(data);
        }
        // Si data tiene la estructura { status, data }
        else if (data.status === 'success' && Array.isArray(data.data)) {
          console.log('Data está dentro de data.data:', data.data);
          setProductos(data.data);
        }
        // Si data tiene otra estructura, mostrarla
        else {
          console.log('Estructura de data inesperada:', typeof data, data);
          message.error('Error en el formato de la respuesta');
        }
      } catch (error) {
        console.error('Error completo:', error);
        message.error('ERROR AL CARGAR LOS PRODUCTOS');
        
        // Datos de ejemplo en caso de error
        setProductos([
          { id: 1, nombre: 'FIBRA', descripcion: 'Fibra de algodón' },
          { id: 2, nombre: 'SEMILLA', descripcion: 'Semilla de algodón' },
          { id: 3, nombre: 'DESPERDICIO', descripcion: 'Desperdicio de algodón' }
        ]);
      } finally {
        setLoadingProductos(false);
      }
    };

    cargarProductos();
  }, [getAccessTokenSilently]);

  const handleTicketBlur = async () => {
    if (!ticketBalanza.trim()) {
      return;
    }

    if (!productoSeleccionado) {
      message.error('Por favor seleccione un producto primero');
      return;
    }

    if (!empresaId || !sucursalId) {
      message.error('Error: No se pudo obtener la información de empresa o sucursal');
      console.error('empresaId:', empresaId, 'sucursalId:', sucursalId);
      return;
    }

    console.log('Datos para validación:', {
      ticketBalanza,
      productoSeleccionado,
      empresaId,
      sucursalId
    });

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      
      // Llamada a la API con el nuevo formato
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/a_produccion_automatica/${ticketBalanza}/${productoSeleccionado}/${empresaId}/${sucursalId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data: TicketResponse = await response.json();
      console.log('Respuesta completa:', data);
      
      if (data.status === 'success' && Array.isArray(data.data)) {
        console.log('Tickets obtenidos:', data.data);
        message.success(`Se encontraron ${data.data.length} tickets`);
        
        // Agregar ID interno único y propiedad selected a cada ticket
        const ticketsWithSelection = data.data.map((ticket, index) => ({
          ...ticket,
          id: index + 1, // ID interno único que empieza desde 1
          selected: !ticket.desmotado // Solo seleccionar los que NO están desmotados
        }));
        
        // Obtener solo los IDs de los rollos disponibles (no desmotados)
        const availableIds = ticketsWithSelection
          .filter(ticket => !ticket.desmotado)
          .map(ticket => ticket.id);
        
        setTicketsData(ticketsWithSelection);
        setSelectedTickets(availableIds); // Solo seleccionar los disponibles
        setSelectAll(availableIds.length > 0); // Marcar "Seleccionar Todos" como activo solo si hay disponibles
        
      } else {
        message.error(data.message || 'Error al obtener los tickets');
        setTicketsData([]);
        setSelectedTickets([]);
        setSelectAll(false);
      }
    } catch (error) {
      console.error('Error al obtener tickets:', error);
      message.error('Error al obtener los tickets');
      setTicketsData([]);
      setSelectedTickets([]);
      setSelectAll(false);
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección individual de tickets
  const handleTicketSelection = (ticketId: number, checked: boolean) => {
    console.log('handleTicketSelection llamado:', { ticketId, checked, selectedTickets });
    
    // Verificar si el ticket está desmotado
    const ticket = ticketsData.find(t => t.id === ticketId);
    if (ticket?.desmotado) {
      console.log('No se puede seleccionar un rollo desmotado');
      return; // No permitir seleccionar rollos desmotados
    }
    
    if (checked) {
      // Agregar el ticket a la selección
      setSelectedTickets(prev => {
        // Si el ticket ya está seleccionado, no agregarlo de nuevo
        if (prev.includes(ticketId)) {
          console.log('Ticket ya seleccionado, no agregando duplicado');
          return prev;
        }
        const newSelection = [...prev, ticketId];
        console.log('Nueva selección (agregar):', newSelection);
        return newSelection;
      });
    } else {
      // Remover el ticket de la selección
      setSelectedTickets(prev => {
        const newSelection = prev.filter(ticket => ticket !== ticketId);
        console.log('Nueva selección (remover):', newSelection);
        return newSelection;
      });
    }
  };

  // Manejar selección de todos
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      // Seleccionar solo los rollos disponibles (no desmotados)
      const availableIds = ticketsData
        .filter(ticket => !ticket.desmotado)
        .map(ticket => ticket.id);
      setSelectedTickets(availableIds);
    } else {
      setSelectedTickets([]);
    }
  };

  // Efecto para actualizar el estado de "Seleccionar Todos" cuando cambia selectedTickets
  useEffect(() => {
    // Solo considerar los rollos disponibles (no desmotados)
    const availableIds = ticketsData
      .filter(ticket => !ticket.desmotado)
      .map(ticket => ticket.id);
    
    const allAvailableSelected = availableIds.length > 0 && 
      availableIds.every(id => selectedTickets.includes(id));
    
    setSelectAll(allAvailableSelected);
    
    console.log('useEffect - actualizando selectAll:', {
      selectedTickets,
      availableIds,
      allAvailableSelected,
      selectedTicketsLength: selectedTickets.length,
      availableIdsLength: availableIds.length,
      ticketsDataLength: ticketsData.length
    });
  }, [selectedTickets, ticketsData]);

  const handleContinuar = () => {
    if (!productoSeleccionado) {
      message.error('Por favor seleccione un producto');
      return;
    }

    if (!ticketBalanza.trim()) {
      message.error('Por favor ingrese el ticket de balanza');
      return;
    }

    if (selectedTickets.length === 0) {
      message.error('Por favor seleccione al menos un ticket');
      return;
    }

    // Obtener el nombre del producto seleccionado
    const productoSeleccionadoObj = productos.find(p => p.id === productoSeleccionado);
    const nombreProducto = productoSeleccionadoObj?.descripcion || productoSeleccionadoObj?.nombre || '';
    
    // Filtrar solo los tickets seleccionados
    const ticketsSeleccionados = ticketsData.filter(ticket => 
      selectedTickets.includes(ticket.id)
    );
    
    // Log de los datos que se van a pasar
    console.log('Datos para continuar:', {
      productoId: productoSeleccionado,
      productoNombre: nombreProducto,
      ticketBalanza,
      ticketsSeleccionados,
      cantidadTickets: selectedTickets.length
    });

    // Obtener el producto seleccionado para acceder a su nivel
    const productoSeleccionadoData = productos.find(p => p.id === productoSeleccionado);
    
    // Obtener información del cliente y productor del primer ticket seleccionado
    const primerTicket = ticketsSeleccionados[0];
    const cliente = primerTicket?.cliente || 'CLIENTE';
    const cliente_id = primerTicket?.cliente_id || 1; // ID del cliente
    const productor = primerTicket?.productor || cliente; // Si no hay productor, usar cliente
    const productor_id = primerTicket?.productor_id || 1; // ID del productor
    
    // Contar rollos seleccionados
    const cantidadRollos = ticketsSeleccionados.length;
    
    // Log de los datos del cliente y productor
    console.log('Datos del cliente y productor:', {
      cliente,
      cliente_id,
      productor,
      productor_id
    });
    
    // Navegar directamente al componente de desmote con las variables
    navigate('/desmote', { 
      state: { 
        producto_id: productoSeleccionado,
        producto: nombreProducto,
        nivel: productoSeleccionadoData?.nivel || '0',
        tickets: ticketsSeleccionados,
        ticketBalanza,
        cliente,
        cliente_id,
        productor,
        productor_id,
        cantidadRollos
      } 
    });
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      title: 'Seleccionar',
      key: 'select',
      width: 80,
      render: (_: any, record: TicketData) => (
        <Checkbox
          checked={selectedTickets.includes(record.id)}
          onChange={(e) => handleTicketSelection(record.id, e.target.checked)}
          disabled={record.desmotado} // Deshabilitar si está desmotado
        />
      ),
    },
    {
      title: 'N° Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 100,
      render: (text: number) => <Text strong>{text}</Text>,
    },
    {
      title: 'Rollo',
      dataIndex: 'rollo_identificacion',
      key: 'rollo_identificacion',
      width: 120,
      render: (text: string) => <Text>{text || '-'}</Text>,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: 200,
      render: (text: string) => <Text>{text}</Text>,
    },
    {
      title: 'Kilos Netos',
      dataIndex: 'kilos_netos',
      key: 'kilos_netos',
      width: 120,
      render: (text: number, record: TicketData) => (
        <Text strong style={{ 
          color: record.desmotado ? '#999' : '#1890ff',
          textDecoration: record.desmotado ? 'line-through' : 'none'
        }}>
          {text.toLocaleString()} kg
        </Text>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      render: (_: any, record: TicketData) => (
        <Tag color={record.desmotado ? 'red' : 'green'}>
          {record.desmotado ? 'Desmotado' : 'Disponible'}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '22px', 
        fontWeight: 600, 
        marginBottom: '12px' 
      }}>
        Producción Automática
      </h1>

      <style>
        {`
          .desmotado-row {
            background-color: #f5f5f5 !important;
            opacity: 0.7;
          }
          .desmotado-row:hover {
            background-color: #f0f0f0 !important;
          }
        `}
      </style>

      <Card
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '13px',
            fontWeight: 600,
            padding: '8px 16px'
          }
        }}
        title="Desmote Automático"
      >
      <div style={{ padding: '12px' }}>

        {/* Formulario */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '16px'
        }}>
          {/* Select de Productos */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Producto <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <Select
              value={productoSeleccionado}
              onChange={setProductoSeleccionado}
              placeholder="Seleccione un producto"
              loading={loadingProductos}
              size="middle"
              style={{ width: '100%', fontSize: '13px' }}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {productos.map((producto) => (
                <Option key={producto.id} value={producto.id}>
                  {producto.descripcion || producto.nombre}
                </Option>
              ))}
            </Select>
          </div>

          {/* Input Ticket Balanza */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Ticket Balanza <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <Input
                type="number"
                value={ticketBalanza}
                onChange={(e) => {
                  setTicketBalanza(e.target.value);
                  setTicketsData([]); // Limpiar datos cuando cambie el ticket
                  setSelectedTickets([]); // Resetear selecciones
                  setSelectAll(false);
                }}
                onBlur={handleTicketBlur}
                placeholder="Ingrese el ticket de balanza"
                size="middle"
                style={{ width: '100%', textAlign: 'center', fontSize: '13px' }}
              />
              {ticketBalanza && (
                <div style={{ 
                  position: 'absolute', 
                  top: '-22px', 
                  right: '0',
                }}>
                  {loading ? (
                    <Tag color="blue" style={{ fontSize: '11px', padding: '2px 8px' }}>⏳ Cargando...</Tag>
                  ) : ticketsData.length > 0 ? (
                    <Tag color="green" style={{ fontSize: '11px', padding: '2px 8px' }}>✓ {ticketsData.length} tickets encontrados</Tag>
                  ) : (
                    <Tag color="orange" style={{ fontSize: '11px', padding: '2px 8px' }}>⚠️ Sin tickets</Tag>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

            {/* Tabla de Tickets */}
            {ticketsData.length > 0 && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                marginTop: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <Text strong style={{
                    fontSize: '13px',
                    color: '#262626'
                  }}>
                    Tickets y Rollos ({ticketsData.length})
                  </Text>

                  <Space size="small" wrap>
                    <Checkbox
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ fontSize: '12px' }}
                    >
                      Seleccionar Todos
                    </Checkbox>
                    <Tag color="blue" style={{ fontSize: '11px', padding: '2px 8px' }}>
                      {selectedTickets.length} seleccionados
                    </Tag>
                    <Tag color="green" style={{ fontSize: '11px', padding: '2px 8px' }}>
                      {ticketsData.filter(t => !t.desmotado).length} disponibles
                    </Tag>
                    <Tag color="red" style={{ fontSize: '11px', padding: '2px 8px' }}>
                      {ticketsData.filter(t => t.desmotado).length} desmotados
                    </Tag>
                  </Space>
                </div>
                
                <Table
                  dataSource={ticketsData}
                  columns={columns}
                  pagination={false}
                  size="small"
                  scroll={{ y: 200 }}
                  rowKey="numero_ticket"
                  rowClassName={(record) => record.desmotado ? 'desmotado-row' : ''}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '6px'
                  }}
                />
              </div>
            )}

        {/* Botón Continuar */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleContinuar}
            disabled={selectedTickets.length === 0 || !productoSeleccionado}
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '180px',
              height: '32px',
              fontSize: '13px',
              opacity: (selectedTickets.length > 0 && productoSeleccionado) ? 1 : 0.6
            }}
          >
            {selectedTickets.length === 0 ? 'Seleccione tickets' : `Continuar con ${selectedTickets.length} tickets`}
          </Button>
        </div>
      </div>
      </Card>
    </div>
  );
};

export default PrevioDesmote;
